import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  FormControl, FormLabel, FormHelperText,
  Button, Typography, Alert, Box, List, ListItem,
} from "@mui/joy";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const AddressPage = ({ setPage, setOpen, setAddress }) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debouncedInput = useDebouncedValue(inputValue, 300);

  useEffect(() => {
    if (!debouncedInput || debouncedInput.length < 3 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      return;
    }
    const encoded = encodeURIComponent(debouncedInput);
    fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json` +
      `?access_token=${MAPBOX_TOKEN}&country=US&types=address&autocomplete=true&limit=5`
    )
      .then((r) => r.json())
      .then((data) => {
        setSuggestions(data.features || []);
        setShowSuggestions((data.features || []).length > 0);
        setActiveIndex(-1);
      })
      .catch(() => setSuggestions([]));
  }, [debouncedInput]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectSuggestion = (feature) => {
    setInputValue(feature.place_name);
    setSuggestions([]);
    setShowSuggestions(false);
    if (error) setError(false);
  };

  const handleContinue = () => {
    if (!inputValue.trim()) {
      setError(true);
      return;
    }
    setAddress(inputValue);
    setPage("stance");
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) {
      if (e.key === "Enter") handleContinue();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        selectSuggestion(suggestions[activeIndex]);
      } else {
        handleContinue();
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <>
      <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#171a1c", mb: "6px" }}>
        Find Your Representative
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "#535862", lineHeight: 1.6, mb: "20px" }}>
        Enter your address so we can identify your district representative and
        pre-fill a message on your behalf.
      </Typography>

      <Alert
        variant="soft"
        color="neutral"
        size="sm"
        startDecorator={<LockOutlinedIcon sx={{ fontSize: "16px" }} />}
        sx={{ mb: "24px", fontSize: "13px", color: "#535862" }}
      >
        Your address is used only to find your representative and is never stored.
      </Alert>

      <FormControl error={error}>
        <FormLabel sx={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
          Street address
        </FormLabel>

        <Box ref={wrapperRef} sx={{ position: "relative" }}>
          <input
            autoComplete="off"
            placeholder="123 Main St, City, State, ZIP"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError(false);
            }}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            onKeyDown={handleKeyDown}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontSize: "15px",
              fontFamily: "inherit",
              padding: "8px 12px",
              borderRadius: "6px",
              border: error ? "1.5px solid #c1121f" : "1.5px solid #d0d5dd",
              outline: "none",
              color: "#171a1c",
              background: "white",
              lineHeight: 1.5,
            }}
            onFocusCapture={(e) => {
              e.target.style.borderColor = "#0075b2";
              e.target.style.boxShadow = "0 0 0 3px rgba(0,117,178,0.15)";
            }}
            onBlurCapture={(e) => {
              e.target.style.borderColor = error ? "#c1121f" : "#d0d5dd";
              e.target.style.boxShadow = "none";
            }}
          />

          {showSuggestions && suggestions.length > 0 && (
            <List
              size="sm"
              sx={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                zIndex: 9999,
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                p: "4px",
                m: 0,
              }}
            >
              {suggestions.map((feature, i) => (
                <ListItem
                  key={feature.id}
                  onMouseDown={(e) => { e.preventDefault(); selectSuggestion(feature); }}
                  sx={{
                    borderRadius: "4px",
                    cursor: "pointer",
                    px: "10px",
                    py: "8px",
                    gap: "8px",
                    backgroundColor: i === activeIndex ? "rgba(0,117,178,0.08)" : "transparent",
                    "&:hover": { backgroundColor: "rgba(0,117,178,0.06)" },
                    alignItems: "flex-start",
                  }}
                >
                  <LocationOnOutlinedIcon sx={{ fontSize: "16px", color: "#0075b2", mt: "2px", flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#171a1c", lineHeight: 1.3 }}>
                      {feature.text}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280", lineHeight: 1.3 }}>
                      {feature.place_name.replace(`${feature.text}, `, "")}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {error && (
          <FormHelperText>
            <InfoOutlined sx={{ fontSize: "15px" }} />
            Please enter a full address including street, city, state, and ZIP.
          </FormHelperText>
        )}
      </FormControl>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "24px" }}>
        <Button
          fullWidth
          size="lg"
          onClick={handleContinue}
          sx={{
            backgroundColor: "#003049",
            color: "white",
            fontWeight: 600,
            fontSize: "16px",
            borderRadius: "6px",
            minHeight: "48px",
            "&:hover": { backgroundColor: "#00243a" },
          }}
        >
          Continue
        </Button>
        <Button
          fullWidth
          variant="plain"
          color="neutral"
          onClick={() => setOpen(false)}
          sx={{ fontSize: "15px", fontWeight: 500 }}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
};

AddressPage.propTypes = {
  setOpen: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setAddress: PropTypes.func.isRequired,
};

export default AddressPage;
